import React from 'react';
import { Button, Card, Input, InputNumber, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CONTROL,
  variantProblems,
  type DraftForm,
  type ParamRow,
  type ParamType,
  type VariantForm,
} from './draftForm';

interface Props {
  form: DraftForm;
  onChange: (variants: VariantForm[]) => void;
}

const MAX_VARIANTS = 5;

const TYPE_OPTIONS: { value: ParamType; label: string }[] = [
  { value: 'bool', label: 'true / false' },
  { value: 'int', label: 'whole number' },
  { value: 'float', label: 'decimal' },
  { value: 'string', label: 'text' },
];

const emptyValue = (type: ParamType) => (type === 'bool' ? 'false' : '');

/** Every variant sets the same param keys, so key, type, add and remove apply to all of them. */
export const VariantsEditor: React.FC<Props> = ({ form, onChange }) => {
  const variants = form.variants;
  const isFlag = form.kind === 'flag';
  const params: ParamRow[] = variants[0]?.params ?? [];
  const total = Math.round(variants.reduce((n, v) => n + v.weightPercent * 10, 0)) / 10;
  const problems = variantProblems(form);

  const setVariant = (i: number, patch: Partial<VariantForm>) =>
    onChange(variants.map((v, j) => (j === i ? { ...v, ...patch } : v)));

  const setParamEverywhere = (p: number, patch: Partial<ParamRow>) =>
    onChange(
      variants.map((v) => ({
        ...v,
        params: v.params.map((row, j) =>
          j === p
            ? {
                ...row,
                ...patch,
                // A new type resets values the old type allowed but the new one does not read.
                ...(patch.type && patch.type !== row.type ? { value: emptyValue(patch.type) } : {}),
              }
            : row
        ),
      }))
    );

  const setValue = (i: number, p: number, value: string) =>
    setVariant(i, {
      params: variants[i].params.map((row, j) => (j === p ? { ...row, value } : row)),
    });

  const addParam = () =>
    onChange(
      variants.map((v) => ({ ...v, params: [...v.params, { key: '', type: 'int', value: '' }] }))
    );

  const removeParam = (p: number) =>
    onChange(variants.map((v) => ({ ...v, params: v.params.filter((_, j) => j !== p) })));

  const addVariant = () =>
    onChange([
      ...variants,
      {
        key: `v${variants.length + 1}`,
        weightPercent: 0,
        params: params.map((row) => ({ ...row, value: emptyValue(row.type) })),
      },
    ]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-section="variants">
      {variants.map((v, i) => (
        <Card
          key={i}
          size="small"
          data-variant={v.key}
          title={
            <Space wrap>
              <Input
                id={`expv2-variant-key-${i}`}
                aria-label="Variant key"
                value={v.key}
                disabled={isFlag || v.key === CONTROL}
                style={{ width: 160 }}
                onChange={(e) => setVariant(i, { key: e.target.value })}
              />
              {!isFlag && (
                <InputNumber
                  id={`expv2-variant-weight-${i}`}
                  aria-label="Weight"
                  min={0}
                  max={100}
                  step={0.1}
                  addonAfter="%"
                  value={v.weightPercent}
                  onChange={(n) => setVariant(i, { weightPercent: n ?? 0 })}
                />
              )}
            </Space>
          }
          extra={
            !isFlag &&
            v.key !== CONTROL && (
              <Button
                size="small"
                icon={<DeleteOutlined />}
                aria-label={`Remove variant ${v.key}`}
                onClick={() => onChange(variants.filter((_, j) => j !== i))}
              />
            )
          }
        >
          {v.params.length === 0 && (
            <Typography.Text type="secondary">No params yet.</Typography.Text>
          )}
          {v.params.map((row, p) => (
            <Space key={p} wrap style={{ marginBottom: 6 }} data-param={row.key}>
              <Input
                id={`expv2-param-key-${i}-${p}`}
                aria-label="Param key"
                placeholder="param_key"
                value={row.key}
                disabled={i > 0}
                style={{ width: 180 }}
                onChange={(e) => setParamEverywhere(p, { key: e.target.value })}
              />
              <Select<ParamType>
                id={`expv2-param-type-${i}-${p}`}
                aria-label="Param type"
                value={row.type}
                disabled={i > 0}
                options={TYPE_OPTIONS}
                style={{ width: 140 }}
                onChange={(type) => setParamEverywhere(p, { type })}
              />
              {row.type === 'bool' ? (
                <Select
                  id={`expv2-param-value-${i}-${p}`}
                  aria-label="Param value"
                  value={row.value}
                  options={[
                    { value: 'true', label: 'true' },
                    { value: 'false', label: 'false' },
                  ]}
                  style={{ width: 120 }}
                  onChange={(value) => setValue(i, p, value)}
                />
              ) : (
                <Input
                  id={`expv2-param-value-${i}-${p}`}
                  aria-label="Param value"
                  value={row.value}
                  style={{ width: 220 }}
                  onChange={(e) => setValue(i, p, e.target.value)}
                />
              )}
              {i === 0 && (
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={`Remove param ${row.key}`}
                  onClick={() => removeParam(p)}
                />
              )}
            </Space>
          ))}
        </Card>
      ))}

      <Space wrap>
        <Button icon={<PlusOutlined />} onClick={addParam}>
          Add param
        </Button>
        {!isFlag && variants.length < MAX_VARIANTS && (
          <Button icon={<PlusOutlined />} onClick={addVariant}>
            Add variant
          </Button>
        )}
        {!isFlag && (
          <Typography.Text data-weight-total={total} type={total === 100 ? 'secondary' : 'danger'}>
            Total {total}%
          </Typography.Text>
        )}
      </Space>
      <Typography.Text type="secondary">
        Params are set in the first variant and copied to every variant; each variant sets its own
        values.
      </Typography.Text>
      {problems.map((p) => (
        <Typography.Text key={p} type="danger" data-problem>
          {p}
        </Typography.Text>
      ))}
    </Space>
  );
};
